<template>
	<div >
		<label
			:for="fieldId"
			:data-field-name="fieldName"
			:id="'scrolltarget-' + fieldName"
		>
			{{ label }}
		</label>

		<template v-if="isPostalCodeField">

			<div v-if="enableAddressLookup" :class="{'c-search-dropdown c-search-dropdown--multi-column c-search-dropdown--borders' : isPostalCodeField}">
				<PostalCodeWithSearch
					:value="formData[fieldName]"
					:placeholder="placeholderText"
					:id="fieldId"
					:fieldName="fieldName"
					:name="name"
					:errors="errors"
					:hasError="hasError"
					:cannotFindAddressLabel="cannotFindAddressLabel"
					:maxSuggestions="maxSuggestions"
					@validateField="validateField"
					@onBlur="onBlur"
				/>
			</div>

			<input
				v-else
				:id="fieldId"
				:type="clientSideInputType"
				:name="name"
				:data-field-name="fieldName"
				v-model="formData[fieldName]"
				:placeholder="placeholderText"
				:required="required"
				:maxLength="maxLength"
				:readonly="readOnly"
				:disabled="readOnly"
				:aria-invalid="hasError(fieldName) || null"
				:aria-errormessage="'error_' + fieldId"
				@focus="onFocus"
				@blur="onBlur"
				data-testid="postalCode"
			/>
		</template>

		<template v-else>
			<input
				:id="fieldId"
				:type="clientSideInputType"
				:name="name"
				:data-field-name="fieldName"
				v-model="formData[fieldName]"
				:readonly="readOnly"
				:disabled="readOnly"
				:placeholder="placeholderText"
				:required="required"
				:maxLength="maxLength"
				:aria-invalid="hasError(fieldName) || null"
				:aria-errormessage="'error_' + fieldId"
				@focus="onFocus"
				@blur="onBlur"
			/>
		</template>

		<div
			:id="'error_' + fieldId"
			class="error-box"
			:data-field-name="fieldName"
			:data-tracking-name="trackingName"
		>
			<span
				class="error show-error validation-error"
				v-if="hasErrorType(fieldName, 'requiredError')"
			>{{ requiredErrorText }}</span>
			<span
				class="error show-error max-length-error"
				v-if="hasErrorType(fieldName, 'maxLengthError')"
			>{{ maxLengthErrorText }}</span>
			<span
				class="error show-error format-error"
				v-if="hasErrorType(fieldName, 'formatError')"
			>{{ formatValidationErrorField }}</span>
			<span
				class="error show-error api-validation-error"
				v-if="hasErrorType(fieldName, 'apiValidationError')"
			>{{ apiValidationErrorField }}</span>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import PostalCodeWithSearch from './postalCodeWithSearch.vue';
import { trackingFieldName, validateFieldState } from 'multisite/services/checkoutHelperService.js';

const props = defineProps({
	enableAddressLookup: {
		type: Boolean,
		default: false,
	},
	cannotFindAddressLabel: {
		type: String,
		default: '',
	},
	formData: {
		type: Object,
		required: true,
	},
	submitted: Boolean,
	errors: {
		type: Object,
		default: () => {},
	},
	updateFieldError: Function,
	fieldId: {
		type: String,
		default: null,
	},
	label: {
		type: String,
		default: null,
	},
	name: {
		type: String,
		default: null,
	},
	fieldName: {
		type: String,
		default: null,
	},
	clientSideInputType: {
		type: String,
		default: 'text',
	},
	required: {
		type: Boolean,
		default: false,
	},
	maxLength: {
		type: Number,
		default: 0,
	},
	maxLengthErrorText: {
		type: String,
		default: '',
	},
	requiredErrorText: {
		type: String,
		default: '',
	},
	placeholderText: {
		type: String,
		default: '',
	},
	validatePattern: {
		default: null,
	},
	validateAllowPatterns: {
		type: Array,
		default: () => [],
	},
	validateNotAllowPatterns: {
		type: Array,
		default: () => [],
	},
	formatErrorText: {
		type: String,
		default: '',
	},
	readOnly: {
		type: Boolean,
		default: false,
	},
	maxSuggestions: {
		type: Number,
		default: 0,
	},
});

const emit = defineEmits(['blur', 'focus']);

const hasBlur = ref({ [props.fieldName]: false });
const trackingName = ref(trackingFieldName(props.fieldName));

const isPostalCodeField = computed(() => props.fieldName === 'postalCode');
const apiValidationErrorField = computed(() => props.errors[props.fieldName]?.apiValidationError || '');
const formatValidationErrorField = computed(() => props.errors[props.fieldName]?.formatErrorMessage || props.formatErrorText);

const validateField = () => {
  	props.updateFieldError(
		props.fieldName,
			validateFieldState(props.formData[props.fieldName], {
			required: props.required,
			maxLength: props.maxLength,
			validatePattern: props.validatePattern,
			apiValidationError: apiValidationErrorField.value,
			allowPatterns: props.validateAllowPatterns,
			disallowPatterns: props.validateNotAllowPatterns,
		})
	);
};

const hasErrorType = (item, type) => {
	return (hasBlur.value[item] || props.submitted) && props.errors[item]
		? props.errors[item][type]
		: false;
};

const hasError = (item) => {
	return hasBlur.value[item] || props.submitted
		? props.errors[item] && !props.errors[item].isValid
		: false;
};

const onBlur = (event, fieldName) => {
	hasBlur.value[event ? event.target.dataset.fieldName : fieldName] = true;
	emit('blur', event);
};

const onFocus = (event) => {
  	emit('focus', event);
};

watch(
	() => props.formData,
		() => { validateField() },
	{ deep: true }
);

watch(apiValidationErrorField, () => {
  	validateField();
});

onMounted(() => {
  	validateField();
});
</script>
